(ns ui.view.entity-creator.utils
  (:require
   [common.email :as email]
   [clojure.string :as string]
   [common.entity :as entity]
   [common.localization :as loc]
   [common.schema.assets :as assets]
   [common.schema.constraints :as constraints]
   [common.schema.data-types :as data-types]
   [common.schema.entity-creator.utils :as ecu]
   [common.schema.field-spec :as field-spec]
   [common.util :as cu]
   [frontend.logging :as logging]
   [frontend.state.permissions :as f-permissions]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [frontend.updates.entity-scope :as entity-scope]
   [frontend.view.modals.events :as modals]
   [frontend.view.modals.error-message.view :as error-message]
   [ui.state.named-values :as named-values]))

(defn load-selected!
  "Loads the entity that the user selected in a picker from the backend, with
   `entity-scope/load-entity!`, and guards the load.

   `loading*` is an atom that holds the id of the entity that is loading, or
   nil. The view reads it to show that something is loading.

   - A second call for the id that is loading does nothing, so a double click
     does not fetch twice.
   - A call for another id starts a new load. The answer of a load that another
     one replaced is ignored. A request that never answers therefore cannot
     block the picker.
   - `loading*` is cleared before `on-loaded` or `on-error` runs. It is also
     cleared when `load-entity!` throws.

   `on-loaded` and `on-error` are those of `load-entity!`."
  [{:keys [loading* entity-id on-loaded on-error]}]
  (when-not (= entity-id @loading*)
    (reset! loading* entity-id)
    (let [current? (fn []
                     (= entity-id @loading*))]
      (try
        (entity-scope/load-entity!
         {:entity-id entity-id
          :on-loaded (fn [entity entities]
                       (when (current?)
                         (reset! loading* nil)
                         (on-loaded entity entities)))
          :on-error  (fn [error]
                       (when (current?)
                         (reset! loading* nil)
                         (on-error error)))})
        (catch :default e
          (reset! loading* nil)
          (throw e))))))

(defn first-pipeline
  [entity-type]
  (->> @session/pipelines
       (cu/filter-by :pipeline/entity-type entity-type)
       first))

(defn pipeline-stages
  [pipeline-id]
  (let [stages (->> @session/stages
                    (cu/filter-by :stage/pipeline pipeline-id)
                    (sort-by :common/ord)
                    doall
                    seq)]
    stages))

(defn first-stage
  [pipeline-id]
  (let [stages (pipeline-stages pipeline-id)]
    (first stages)))

(defn first-target-type
  [pipeline-id]
  ;; Note: order not defined. Good enough for this case:
  (->> pipeline-id
       session/get-entity
       :pipeline/target-asset-types
       first))

(defn search-input?
  [data-type]
  (contains? data-types/ref-types data-type))

(defn option-attribute?
  [entity-schema attribute]
  (-> entity-schema
      (get attribute)
      :field/data-type
      search-input?))

(defn option-vals->val
  [val-or-vals]
  (cond (map? val-or-vals)
        (:value val-or-vals)

        (seq val-or-vals)
        (mapv option-vals->val val-or-vals)

        :else
        val-or-vals))

(defn option-values->ids
  "Change `{:value val :label \"Label\"}` values into just `val`"
  [{:keys [entity-schema]} entity]
  (let [option-attributes (->> entity
                               keys
                               (filterv (partial option-attribute? entity-schema)))
        updated-map (-> entity
                        (select-keys option-attributes)
                        (update-vals option-vals->val))]
    (merge entity updated-map)))

(defn- option-label
  "The label of the referenced entity `id`. The label comes from the session when
   the session has the entity. The new grid does not bulk-sync a named-value. The
   label then comes from the shared option rows of `named-value-attribute`.

   The result is never nil. The search editor throws an error on render when a
   value has no label, and the whole creator closes. If the label is not known
   yet, the result is a loading text."
  [{:keys [named-value-attribute id]}]
  (or (entity/display-string (session/get-entity id))
      (some-> (named-values/row-by-id named-value-attribute id)
              (entity/display-string))
      (loc/tr :loading)))

(defn val->option-val
  ([val]
   (val->option-val nil val))
  ([named-value-attribute val]
   (cond (and (map? val)
              (contains? val :value)
              (contains? val :label)) val
         (and (map? val)
              (contains? val :db/id)) (-> val
                                          (assoc
                                           :value val
                                           :label (option-label
                                                   {:named-value-attribute named-value-attribute
                                                    :id                    val})))
         (map? val) val
         (nil? val) nil
         (number? val) {:value val
                        :label (option-label
                                {:named-value-attribute named-value-attribute
                                 :id                    val})}
         (coll? val) (mapv (partial val->option-val named-value-attribute) val)
         :else nil)))

(defn ids->option-values
  "Change `{:value val :label \"Label\"}` values into just `val`"
  [{:keys [entity-schema]} entity]
  (let [option-attributes (->> entity
                               keys
                               (filterv (partial option-attribute? entity-schema)))
        updated-map (->> option-attributes
                         (map (fn [attribute]
                                [attribute
                                 (val->option-val
                                  (get-in entity-schema [attribute :named-value-attribute])
                                  (get entity attribute))]))
                         (into {}))]
    (merge entity updated-map)))

(defn currency-value?
  [entity-schema attribute]
  (-> entity-schema
      (get attribute)
      :field/data-type
      (= :data.type/currency-value)))

(defn currency-symbol?
  [entity-schema attribute]
  (-> entity-schema
      (get attribute)
      :field/data-type
      (= :data.type/currency-symbol)))

(defn missing-currency-symbol?
  [entity attribute]
  (let [sym-a (cu/currency->symbol attribute)
        sym-val (get entity sym-a)]
    (not (keyword? sym-val))))

(defn missing-currency-value?
  [entity attribute]
  (let [cur-a (cu/currency-symbol->currency attribute)
        cur-val (get entity cur-a)]
    (cu/blank-value? cur-val)))

;; note: won't apply directly to editing an existing entity
;; (existing currency-symbol should not be replaced with default)
(defn update-currency-symbols
  "Add default currency-symbol values if missing; remove
   currency-symbols is corresponding currency-value is missing"
  [{:keys [default-currency
           entity-schema]} entity]
  (let [symbols-to-add (->> entity
                            keys
                            (filterv (partial currency-value? entity-schema))
                            (filterv (partial missing-currency-symbol? entity))
                            (mapv cu/currency->symbol))
        symbols-to-remove (->> entity
                               keys
                               (filterv (partial currency-symbol? entity-schema))
                               (filterv (partial missing-currency-value? entity)))]
    (-> (apply dissoc entity symbols-to-remove)
        (merge (zipmap symbols-to-add (repeat default-currency))))))

(defn normalize-entity-edits
  "Prepares a creator's `entity-edits` map for sending to the backend:
   drops blank/empty attribute values, fills in default currency-symbols for
   any currency-value attribute missing one (and clears symbols whose value
   went away), and converts `{:value id :label \"...\"}` option-shapes back
   to raw ids. Shared by the four `start-create` handlers and by
   `entry-criteria`'s `build-move-to-stage-body`."
  [{:keys [entity-schema default-currency]} entity-edits]
  (->> entity-edits
       cu/remove-blank-vals
       (update-currency-symbols {:default-currency default-currency
                                 :entity-schema entity-schema})
       (option-values->ids {:entity-schema entity-schema})))

(defn open-error-modal!
  [error-message]
  (modals/open-modal!
   error-message/modal-key
   {:title
    (loc/tr :attention)
    :body-content
    error-message
    :ok-button-text
    (loc/tr :ok)}))

(def min-processing-time-in-ms 300)

(defn resolve-processing-time
  [start-time now]
  (let [start-time (or start-time 0)
        processing-time-so-far (- now start-time)
        processing-time-left (- min-processing-time-in-ms processing-time-so-far)
        processing-time (cu/clamp 0 min-processing-time-in-ms processing-time-left)]
    processing-time))

(defn fs-mandatory?
  [{:keys [field-config]}]
  (let [{:keys [field/mandatory]} field-config]
    (or (field-spec/system-required-field? field-config)
        (= mandatory :mandatory/always)
        (true? mandatory))))

(defn configured-mandatory?
  [{:keys [field-config]}]
  (let [{:keys [setting]} field-config]
    (= setting :mandatory)))

;; like constraints/validate but instead of returning first failing constraint
;; (as a string), return a vector of
;; {:error-kw :constraint/constraint-keyword :error-message "the error string/val"}
(defn constraints-validate-all
  [{:keys [field-constraints value entity open-entity-fn]}]
  (let [open-fn (or open-entity-fn routing/open-entity)]
    (some->> field-constraints
             (mapv (fn [[c-k _ :as constraint]]
                     (let [empty-mail? (and (= c-k :constraint/valid-email)
                                            (cu/blank-value? value))
                           error
                           (when-not empty-mail?
                             (constraints/_validate- constraint
                                                     (:db/id entity)
                                                     value
                                                     entity
                                                     (partial session/unique-check-map
                                                              false
                                                              (partial
                                                               open-fn))
                                                     loc/tr
                                                     logging/warn))
                           error (if (string? error)
                                   {:error-message error}
                                   error)]
                       (when (map? error)
                         (assoc error
                                :error-kw c-k)))))
             (filterv some?))))

(defn field-errors
  [{:keys [attribute field-schema _field-config value entity open-entity-fn] :as args}]
  (let [mandatory? (or (fs-mandatory? args)
                       (configured-mandatory? args))
        {:keys [field/constraints]} field-schema
        ;; custom validation not expressed in person.cljc:
        constraints (if (and (= attribute :person/universal-email-address)
                             (cu/blank-value? value))
                      (dissoc constraints :constraint/unique-tenant-ignore-case)
                      constraints)
        fs-validations (constraints-validate-all
                        {:field-constraints constraints
                         :value value
                         :entity entity
                         :open-entity-fn open-entity-fn})]
    (->> fs-validations
         (into [(when (and mandatory?
                           (cu/blank-value? value))
                  {:error-kw :constraints/mandatory
                   :error-message "Cannot be empty"})])
         (filterv some?)
         not-empty)))

(defn try-parse-contact-info
  [s]
  (let [parts (string/split (str s) #"\s+")
        email (->> parts
                   (filter email/valid-email-pattern?)
                   (first))
        name-parts (->> parts
                        (remove (fn [p]
                                  (= p email))))
        first-name (first name-parts)
        last-name (string/join " " (rest name-parts))]
    (cond-> {}
      (not (cu/blank-value? first-name))
      (assoc :person/first-name first-name)

      (not (cu/blank-value? last-name))
      (assoc :person/last-name last-name)

      (not (cu/blank-value? email))
      (assoc :person/universal-email-address email)

      true
      not-empty)))

(def froppo-targets
  #{:entity.type/company :entity.type/person
    ;;later:
    #_:entity.type/fof-asset})

(def all-asset-types (set assets/all-asset-types))
(def all-customer-types (set assets/customer-types))

(defn target-type-of?
  "True when `entity-type` is a valid target (asset / customer / legal-entity)
   for the pipeline's opportunity type."
  [pipeline-entity-type entity-type]
  (case pipeline-entity-type
    :entity.type/fundraising-opportunity (contains? froppo-targets entity-type)
    :entity.type/exit-opportunity (contains? froppo-targets entity-type)
    :entity.type/oppo (contains? all-asset-types entity-type)
    :entity.type/sales-opportunity (contains? all-customer-types entity-type)
    false))

(defn target-profile-type-of
  "The target-profile entity-type for the pipeline, or nil if the pipeline
   doesn't use a target-profile. Currently always `:entity.type/investor`
   when applicable."
  [pipeline]
  (let [pipeline-entity-type (:pipeline/entity-type pipeline)
        investor-only-sales? (= (set (:pipeline/target-asset-types pipeline))
                                #{:entity.type/investor})]
    (case pipeline-entity-type
      :entity.type/fundraising-opportunity :entity.type/investor
      :entity.type/exit-opportunity :entity.type/investor
      :entity.type/sales-opportunity (when investor-only-sales?
                                       :entity.type/investor)
      nil)))

(defn pipeline-role
  "How `entity-type` relates to `pipeline`:
   - `:main` — the opportunity itself
   - `:target` — the opportunity's target (asset / customer / legal-entity)
   - `:target-profile` — the investor profile attached to the target
   - `nil` — unrelated (no pipeline, or entity-type doesn't fit)"
  [pipeline entity-type]
  (let [pipeline-entity-type (:pipeline/entity-type pipeline)]
    (cond
      (not pipeline-entity-type) nil
      (= pipeline-entity-type entity-type) :main
      (= (target-profile-type-of pipeline) entity-type) :target-profile
      (target-type-of? pipeline-entity-type entity-type) :target
      :else nil)))

(defn get-config-path
  "Resolves the config-path inside a pipeline's creator-config (or the tenant
   global config when no pipeline) for the entity being created. Dispatches on
   `pipeline-role`: `:main` → main-config-path of the opportunity, `:target` →
   target-config-path (entity-type goes into the target slot, opportunity-type
   becomes the entity-type passed to `resolve-config-paths`), `:target-profile`
   → target-profile-config-path. Falls back to main-config-path of the entity
   itself when there's no opportunity-context."
  [{:keys [entity-type target-type pipeline]}]
  (let [pipeline-entity-type (:pipeline/entity-type pipeline)
        role (pipeline-role pipeline entity-type)
        paths (ecu/resolve-config-paths
               (case role
                 :main {:entity-type entity-type
                        :target-type target-type
                        :pipeline pipeline}
                 :target {:entity-type pipeline-entity-type
                          :target-type entity-type
                          :pipeline pipeline}
                 :target-profile {:entity-type pipeline-entity-type
                                  :target-type target-type
                                  :pipeline pipeline}
                 {:entity-type entity-type
                  :target-type target-type
                  :pipeline pipeline}))]
    (case role
      :target (:target-config-path paths)
      :target-profile (:target-profile-config-path paths)
      (:main-config-path paths))))

(defn resolve-entity-config
  [{:keys [raw-config pipeline stages system-mandatory config-path
           entity-type target-type]}]
  (let [config-path (or config-path
                        ;;^note: config-path must be passed for this to
                        ;; work for target-profiles (i.e. froppo's investor-profile)
                        (get-config-path {:entity-type entity-type
                                          :target-type target-type
                                          :pipeline pipeline}))
        et-config (get-in raw-config config-path)
        config-fn (if (seq stages)
                    (fn [attribute setting]
                      {:attribute attribute
                       :stages
                       (->> stages
                            (mapv (fn [{:keys [db/id]}]
                                    [id {:setting
                                         setting}]))
                            (into {}))})
                    (fn [attribute setting]
                      {:attribute attribute
                       :create {:setting setting}}))
        et-config (or (not-empty et-config)
                      (ecu/default-config
                       {:config-fn config-fn
                        :entity-type entity-type}))
        config-attributes (->> et-config
                               :fields
                               (mapv :attribute)
                               set)
        config-mandatory-fn #(config-fn % :mandatory)
        missing-mandatory (->> system-mandatory
                               (remove (fn [a]
                                         (contains? config-attributes a)))
                               (mapv config-mandatory-fn))
        et-config (if (seq missing-mandatory)
                    (update et-config :fields
                            (fn [fields]
                              (into missing-mandatory fields)))
                    et-config)
        et-config (ecu/ensure-protected-fields
                   {:config et-config
                    :config-fn config-fn
                    :entity-type entity-type})]
    (if stages
      (ecu/resolve-fields-stage-config
       {:system-mandatory system-mandatory
        :config et-config
        :stages stages})
      (ecu/resolve-fields-create-config
       {:system-mandatory system-mandatory
        :config et-config}))))

(defn hidden-field?
  [{:keys [attribute entity-type field-config field-schema pipeline] :as params}]
  (let [{:keys [setting]} field-config
        {:keys [hidden?]} field-schema
        fs-mandatory? (field-spec/system-required-or-legacy-always-mandatory-field?
                       field-schema)
        elsewhere? (ecu/field-handled-elsewhere? params [attribute field-schema])
        system-field? (contains? ecu/system-fields attribute)]
    (or (nil? field-schema)
        system-field?
        elsewhere?
        hidden?
        (not (f-permissions/field-read-access? attribute entity-type pipeline))
        (and (contains? #{:not-shown :use-default-hidden}
                        setting)
             (not fs-mandatory?)))))

(defn read-only-field?
  [{:keys [attribute entity-type pipeline]}]
  (and (f-permissions/field-read-access? attribute entity-type pipeline)
       (not (f-permissions/field-write-access? attribute entity-type pipeline))))

(defn resolve-options
  [{:keys [field-schema]}]
  (let [{:keys [value-type options]} field-schema]
    ;; ref-types handled by opensearch
    (cond (= value-type :keyword)
          (->> options
               (mapv (fn [{:keys [key keyword label]}]
                       {:value (or keyword key)
                        :label label})))

          :else
          nil)))

(defn search-editor-args
  "The arguments that the search editor (open-search) needs for a field.

   `:creation-pipeline-id` is the pipeline of the creator. The search editor uses
   it to offer only the options that are active in that pipeline. Without it, an
   option that is archived in the pipeline is offered."
  [{:keys [entity-types select-entity-types not-clearable?
           os-filter-terms-fn pipeline-id]}]
  {:field/entity-types        entity-types
   :field/select-entity-types select-entity-types
   :field/not-clearable?      not-clearable?
   :field/os-filter-terms-fn  os-filter-terms-fn
   :creation-pipeline-id      pipeline-id})

(defn allow-create?
  [{:keys [field-schema entity-type attribute pipeline-id]}]
  (let [{:keys [create-on-fly?]} field-schema
        config-access? (f-permissions/field-option-create-access?
                        attribute
                        entity-type
                        pipeline-id)]
    ;; testing create-on-fly:
    (or (contains? #{:investor/legal-entity
                     :fof-asset/gp
                     :holding/target-asset
                     :fund-commitment/investor
                     :sale-agreement/customer} attribute)
        (and create-on-fly?
             config-access?))))

(defmulti enrich-initial-values
  "Computes create-time defaults seeded into a new creator's `entity-edits`.
   Per-entity-type methods `cond->` over the caller's `:initial-values` base
   and prefill values like status / pipeline / stage / owner / fund.

   Create-only: `resolve-initial-edits` skips this entirely when an existing
   `:entity` is being edited — those defaults would otherwise silently shadow
   real entity values for attributes the user hasn't yet touched."
  (fn [{:keys [entity-type]}]
    entity-type))

(defmethod enrich-initial-values
  :default
  [{:keys [initial-values]}]
  initial-values)

(defn add-default?
  "Prefill `attribute` with a default value when:
   - the caller hasn't already set the attribute in `:initial-values`
   - the current account has write-access to the attribute on `pipeline`;
   - the attribute is configured on the pipeline with a setting that
     accepts a given default: `:use-default-hidden`, `:use-default-locked`
     (not yet implemented)"
  [{:keys [initial-values entity-type pipeline configured-fields]} attribute]
  (and (not (contains? initial-values attribute))
       (f-permissions/field-write-access? attribute entity-type pipeline)
       (contains? #{:mandatory :system-mandatory :optional
                    :use-default-hidden :use-default-locked}
                  (get-in configured-fields
                          [attribute :setting]))))

(defmethod enrich-initial-values
  :entity.type/holding
  [{:keys [initial-values default-account-id] :as args}]
  (cond-> initial-values
    (add-default? args :holding/exit-status)
    (assoc :holding/exit-status :holding.exit-status/not-exited)

    (add-default? args :holding/cur-realized-gains)
    (assoc :holding/cur-realized-gains 0)

    (and default-account-id
         (add-default? args :holding/owner))
    (assoc :holding/owner default-account-id)))

(defmethod enrich-initial-values
  :entity.type/fund-commitment
  [{:keys [initial-values default-account-id] :as args}]
  (cond-> initial-values
    (and default-account-id
         (add-default? args :fund-commitment/owner))
    (assoc :fund-commitment/owner default-account-id)))

(defmethod enrich-initial-values
  :entity.type/company
  [{:keys [initial-values default-account-id] :as args}]
  (cond-> initial-values
    (and default-account-id
         (add-default? args :company/company-owner))
    (assoc :company/company-owner default-account-id)))

(defmethod enrich-initial-values
  :entity.type/holding
  [{:keys [initial-values default-account-id] :as args}]
  (cond-> initial-values
    (and default-account-id
         (add-default? args :holding/owner))
    (assoc :holding/owner default-account-id)))

(defmethod enrich-initial-values
  :entity.type/sale-agreement
  [{:keys [initial-values default-account-id] :as args}]
  (cond-> initial-values
    (and default-account-id
         (add-default? args :sale-agreement/owner))
    (assoc :sale-agreement/owner default-account-id)))

(defmethod enrich-initial-values
  :entity.type/fundraising-opportunity
  [{:keys [initial-values default-account-id pipeline stage] :as args}]
  (let [fund-id (:pipeline/fund pipeline)]
    (cond-> initial-values
      ;; Note: for hidden fields it would probably make more sense to
      ;;  fill the default values in backend
      true
      ;;TODO backend.domain.entity/create-entity-datoms needs to handle these as
      ;;     special case even if no write-access (around line 385, currently explicit
      ;;     list of :oppo/last-pass-comment, :oppo/pass-reason-v2, etc.)
      (assoc :fundraising-opportunity/status :fundraising-opportunity.status/active
             :fundraising-opportunity/pipeline (:db/id pipeline)
             :fundraising-opportunity/opportunity-stage (:db/id stage))
      (and fund-id
           (session/has-fund-access? fund-id)
           (add-default? args :fundraising-opportunity/fund))
      (assoc :fundraising-opportunity/fund fund-id)

      (and default-account-id
           (add-default? args :fundraising-opportunity/owner))
      (assoc :fundraising-opportunity/owner default-account-id))))

(defmethod enrich-initial-values
  :entity.type/sales-opportunity
  [{:keys [initial-values default-account-id pipeline stage] :as args}]
  (cond-> initial-values
    ;; Note: for hidden fields it would probably make more sense to
    ;;  fill the default values in backend
    true
    (assoc :sales-opportunity/status :sales-opportunity.status/active
           :sales-opportunity/pipeline (:db/id pipeline)
           :sales-opportunity/stage (:db/id stage))

    (and default-account-id
         (add-default? args :sales-opportunity/owner))
    (assoc :sales-opportunity/owner default-account-id)))

(defn preferred-named-value-id
  "Returns the named-value id to prefill: `account-preference` if it's a
   usable option for the pipeline, otherwise the first by `:common/ord`.

   Delegates so the entity modal applies the identical rule, and so the options
   resolve on an account that is not bulk-synced them - see
   ui.state.named-values, whose `create-time-prefill-attributes` covers the
   attributes read here."
  [attribute pipeline-id account-preference]
  (named-values/preferred-id attribute pipeline-id account-preference))

(defmethod enrich-initial-values
  :entity.type/oppo
  [{:keys [initial-values default-account-id pipeline stage] :as args}]
  (let [fund-id (:pipeline/fund pipeline)
        pipeline-id (:db/id pipeline)
        account-preferences (-> @session/account :account/preferences)
        lead-type-id (preferred-named-value-id
                      :oppo/lead-type-v2 pipeline-id
                      (:preferred-lead-type account-preferences))
        lead-source-id (preferred-named-value-id
                        :oppo/lead-source-v2 pipeline-id
                        (:preferred-source-category account-preferences))]
    (cond-> initial-values
      ;; Note: for hidden fields it would probably make more sense to
      ;;  fill the default values in backend
      true
      (assoc :oppo/status :oppo.status/ok
             :oppo/pipeline pipeline-id
             :oppo/stage (:db/id stage))

      (and fund-id
           (session/has-fund-access? fund-id)
           (add-default? args :oppo/fund))
      (assoc :oppo/fund fund-id)

      (and lead-type-id
           (add-default? args :oppo/lead-type-v2))
      (assoc :oppo/lead-type-v2 lead-type-id)

      (and lead-source-id
           (add-default? args :oppo/lead-source-v2))
      (assoc :oppo/lead-source-v2 lead-source-id)

      (and default-account-id
           (add-default? args :oppo/owner))
      (assoc :oppo/owner default-account-id))))

(defmethod enrich-initial-values
  :entity.type/exit-opportunity
  [{:keys [initial-values default-account-id pipeline stage] :as args}]
  (cond-> initial-values
    ;; Note: for hidden fields it would probably make more sense to
    ;;  fill the default values in backend
    true
    (assoc :exit-opportunity/status :exit-opportunity.status/active
           :exit-opportunity/pipeline (:db/id pipeline)
           :exit-opportunity/stage (:db/id stage))

    (and default-account-id
         (add-default? args :exit-opportunity/owner))
    (assoc :exit-opportunity/owner default-account-id)))
